import React, { useRef, useState } from "react";
import { ErrorMessage, Field, Formik } from "formik";
import * as yup from "yup";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import axios from "../../services/axios";
import ReactMde from "react-mde";
import "react-mde/lib/styles/css/react-mde-all.css";
import isConsultant from "../../services/isConsultant";

// Custom Imports
import Dashboard from "../../layouts/Dashboard";
import { useHistory } from "react-router-dom";
import saveLog from "../../services/saveLog";

const Transparent = styled.label`
  opacity: 0;
`;
const DocumentList = styled.div`
  margin-bottom: 50px;
`;
const DocumentBackground = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  background: #e0e0e0;
  &:hover {
    background: #d1d1d1;
  }
`;
const DocumentIcon = styled.i`
  font-size: 48px;
`;
const DocumentButtonIcon = styled.i`
  font-size: 24px;
`;
const DeleteButton = styled.div`
  margin-left: auto;
  cursor: pointer;
  color: #eba09b;
  &:hover {
    color: #ed6f66;
  }
`;
const DocumentDescriptor = styled.div`
  margin-left: 25px;
`;

function Document({ document, index, deleteDocument }) {
  // Effects and Events
  function onClickDeleteDocument() {
    deleteDocument(index);
  }

  return (
    <div>
    {!isConsultant() && <DocumentBackground>
      <DocumentIcon className="fa fa-file" />
      <DocumentDescriptor>
        <h5>
          {document.title} {document.mandatory && "*"}
        </h5>
        <div>Extensions: {document.extensions}</div>
        <div>File Size: {document.size}mb</div>
      </DocumentDescriptor>
      <DeleteButton onClick={onClickDeleteDocument}>
        <DocumentButtonIcon className="fa fa-trash" />
      </DeleteButton>
    </DocumentBackground>}
    </div>
  );
}

const MutedHint = styled.p`
  color: #bbb;
  font-size: 12px;
`;

function DocumentFormPreview({ values, documents, inputs }) {
  return (
    <>
     {!isConsultant() &&  <div className="card shadow">
        <div className="card-header">
          <h5 className="text-primary">{values.title}</h5>
        </div>
        <div className="card-body">
          <ReactMarkdown>{values.description}</ReactMarkdown>
          {inputs.map((input) => (
            <div className="form-group">
              <label>
                {input.name} {input.mandatory && "(*)"}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={"Please enter " + input.name}
              />
            </div>
          ))}
          {documents.map((document, key) => (
            <div className="form-group" key={key}>
              <label>{document.title}</label>
              <input className="form-control" type="file" />
              <MutedHint>
                Allowed {document.extensions} upto {document.size}mb
              </MutedHint>
            </div>
          ))}
        </div>
        <div className="card-footer">
          <button
            className="btn btn-primary"
            onClick={(e) => alert("This is just the form preview.")}
          >
            Upload
          </button>
        </div>
      </div>}
    </>
  );
}

function DocumentFormsCreate() {
  // Stateful Hooks
  const initialValues = {
    title: "Form Name",
    description: "Form Description",
  };
  const validationSchema = yup.object({
    title: yup.string().min(3).required().label("Title"),
    description: yup.string().min(3).required().label("Description"),
  });
  const [documents, setDocuments] = useState([]);
  const [inputs, setInputs] = useState([]);
  const [error, setError] = useState();
  const [addDocumentError, setAddDocumentError] = useState();
  const titleRef = useRef();
  const mandatoryRef = useRef();
  const extensionsRef = useRef();
  const sizeRef = useRef();

  const inputNameRef = useRef();
  const inputManadatoryRef = useRef();

  const history = useHistory();

  // Effects and Events
  function onClickAddDocument() {
    setAddDocumentError();
    const title = titleRef.current.value;
    const extensions = extensionsRef.current.value;
    const size = sizeRef.current.value;
    const mandatory = mandatoryRef.current.checked;

    if (title === "") {
      setAddDocumentError("Please enter a title");
    } else if (extensions === "") {
      setAddDocumentError("Please enter extensions.");
    } else if (size === "") {
      setAddDocumentError("Please enter a file size");
    } else if (size <= 0) {
      setAddDocumentError("Ha Ha. Nice Try. Enter a positive file size limit.");
    } else {
      setDocuments([...documents, { title, extensions, size, mandatory }]);
    }
  }

  function onClickAddInput() {
    setError();
    const name = inputNameRef.current.value;
    const mandatory = inputManadatoryRef.current.checked;
    if (name === "") {
      setError("Please enter input name");
    } else {
      setInputs([...inputs, { name, mandatory }]);
      inputNameRef.current.value = "";
    }
  }

  function deleteDocument(index) {
    documents.splice(index, 1);
    setDocuments([...documents]);
  }

  async function saveDocumentForm(values) {
    setError();
    if (documents.length + inputs.length === 0) {
      setError("You must add atleast 1 document/input field to continue.");
      return;
    }
    const payload = {
      ...values,
      documents: documents.map((document) => {
        document.extensions = document.extensions.toLowerCase().split(",");
        document.extensions.map((extension) => {
          return extension.trim();
        });
        return document;
      }),
      inputs,
    };
    try {
      saveLog("JOINING_FORM_CREATE", `Created a joining form`);
      const { data } = await axios.post("/documents", payload,{
        headers:{
          Authorization: `Bearer ${localStorage.getItem("user.token")}`
        }});
      history.push("/documents/" + data.id + "/edit");
    } catch (e) {
      console.error(e.response.data);
    }
  }

  return (
    <div>
    {!isConsultant() && <Dashboard title="New Document Form">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={saveDocumentForm}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <div className="row">
            <div className="col-md-6">
              <div className="card shadow">
                <div className="card-header">
                  <h5 className="m-0 font-weight-bold text-primary">
                    Form Options
                  </h5>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <Field name="title" className="form-control" type="text" />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="description">
                      Description <small>(Markdown Supported)</small>
                    </label>
                    <ReactMde
                      value={values.description}
                      onChange={(changes) =>
                        setFieldValue("description", changes)
                      }
                      childProps={{
                        previewButton: {
                          style: {
                            display: "none",
                          },
                        },
                        writeButton: {
                          style: {
                            display: "none",
                          },
                        },
                      }}
                    />
                    {/* <Field name="description" className="form-control" as="textarea" /> */}
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <hr />
                  <label>
                    <strong>Files</strong>
                  </label>
                  {documents.length === 0 ? (
                    <p>Please add one or more documents.</p>
                  ) : (
                    <DocumentList>
                      {documents.map((document, key) => (
                        <Document
                          document={document}
                          key={key}
                          index={key}
                          deleteDocument={deleteDocument}
                        />
                      ))}
                    </DocumentList>
                  )}
                  <hr />
                  <label>
                    <strong>Add a File</strong>
                  </label>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentTitle">Document Name</label>
                        <input
                          ref={titleRef}
                          className="form-control"
                          id="documentTitle"
                          type="text"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentExtension">
                          Allowed Extensions
                        </label>
                        <select
                          ref={extensionsRef}
                          id="documentExtension"
                          className="form-control"
                        >
                          <option value="jpg,png,pdf">
                            Images (jpg, png, pdf)
                          </option>
                          <option value="doc,docx,xls,xlsx,pdf">
                            Documents (doc, docx, xls, xlsx, pdf)
                          </option>
                          <option value="mp4">Video (mp4)</option>
                          <option value="doc,docx,xls,xlsx,pdf,jpg,png,mp4">
                            All Allowed Types
                          </option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentSize">File Size Limit</label>
                        <input
                          ref={sizeRef}
                          className="form-control"
                          id="documentSize"
                          type="number"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <input
                          ref={mandatoryRef}
                          id="mandatory"
                          type="checkbox"
                        />{" "}
                        <label htmlFor="mandatory">Mandatory</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <button
                        className="btn btn-primary btn-block"
                        onClick={onClickAddDocument}
                      >
                        Add File to Form
                      </button>
                    </div>
                    {addDocumentError ? (
                      <div className="col-md-12">
                        <div className="text-danger">{addDocumentError}</div>
                      </div>
                    ) : null}
                  </div>
                  {inputs.length > 0 && (
                    <>
                      <hr />
                      <label>
                        <strong>Inputs</strong>
                      </label>
                    </>
                  )}

                  {inputs.map((input, index) => (
                    <div className="form-group">
                      <label>
                        {input.name} {input.mandatory && "(*)"}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={"Please enter " + input.name}
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              inputs.splice(index, 1);
                              setInputs([...inputs]);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <hr />
                  <label>
                    <strong>Add an Input</strong>
                  </label>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor="inputTitle">Input Name</label>
                      <input
                        ref={inputNameRef}
                        className="form-control"
                        id="inputTitle"
                        type="text"
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <input
                        ref={inputManadatoryRef}
                        id="inputMandatory"
                        type="checkbox"
                      />{" "}
                      <label htmlFor="inputMandatory">Mandatory</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <button
                      className="btn btn-primary btn-block"
                      onClick={onClickAddInput}
                    >
                      Add Input to Form
                    </button>
                  </div>

                  <div className="row"></div>
                  {error ? (
                    <div className="alert alert-danger mt-3">{error}</div>
                  ) : null}
                </div>
                <div className="card-footer">
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Create
                  </button>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <DocumentFormPreview
                values={values}
                documents={documents}
                inputs={inputs}
              />
            </div>
          </div>
        )}
      </Formik>
    </Dashboard>}
    </div>
  );
}

export default DocumentFormsCreate;
